import { createReducer, on } from '@ngrx/store';
import { get, remove, save } from 'src/app/data/utils/localstorage.util';
import {
	ApplicantData,
	ApplicantType,
	AuthorizedCreditDetails,
	PreApplicationCreditData,
	PreApplicationState,
	Product,
	SimulatorData,
} from 'src/app/models/state/pre-application.model';
import { PreApplicationActions } from '../actions/pre-application.actions';

const PREAPPLICATION_KEY = 'PreApplicationState';

const initialState: PreApplicationState = {
	periodicityId: 0,
	preApplicationId: 0,
	folio: null,
	amount: 0,
	rate: 0,
	subProductId: 0,
	creditTerm: 0,
	periodPayment: 0,
	applicantType: null,
	applicantId: 0,
	applicantName: null,
	applicantPhone: '',
	applicantBirthDate: '',
	isApplicantAMember: false,
	isCreditApproved: false,
	authorizedAmount: 0,
	cat: 0,
	email: '',
	otpCode: '',
	applicationId: 0,
	applicantAmountSelected: 0,
	totalAmountToPay: 0,
	productId: 0,
	productName: '',
	segmentId: 0,
	isSocialContributionApplicable: false,
	schoolarshipCertificateId: 0,
	curp: '',
	tdcApplicationId: 0,
	applicantAddresses: [],
	applicationStatus: 0,
	applicationSubStatus: 0,
	preApplicationStatus: 0,
	productCobisId: 'DIGITAL',
};

const getInitialState = (initialState): PreApplicationState => {
	const stateFromLocalStorage = get<PreApplicationState>(PREAPPLICATION_KEY);
	if (stateFromLocalStorage !== null) {
		return stateFromLocalStorage;
	}

	return initialState;
};

export const preApplicationReducer = createReducer(
	getInitialState(initialState),
	on(
		PreApplicationActions.addSubproductid,
		(state, { subProductId }): PreApplicationState => {
			const newState = {
				...state,
				subProductId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addPreapplicationid,
		(state, { preApplicationId }): PreApplicationState => {
			const newState = {
				...state,
				preApplicationId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addApplicantData,
		(state, applicantData: ApplicantData): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				applicantId: applicantData.applicantId,
				applicantName: applicantData.applicantName,
				applicantType: applicantData.applicantType,
				applicantPhone: applicantData.applicantPhone,
				applicantBirthDate: applicantData.applicantBirthDate,
				isApplicantAMember: applicantData.isApplicantAMember,
				email: applicantData.email,
				curp: applicantData.curp,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addProductCobisId,
		(state, { productCobisId }): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				productCobisId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addApplicantContactData,
		(state, { applicantPhone, email }): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				applicantPhone: applicantPhone ?? state.applicantPhone,
				email: email ?? state.email,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addPreapplicatonCreditData,
		(
			state,
			preApplicationCreditDate: PreApplicationCreditData
		): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				folio: preApplicationCreditDate.folio,
				authorizedAmount: preApplicationCreditDate.authorizedAmount,
				rate: preApplicationCreditDate.rate,
				isCreditApproved: preApplicationCreditDate.isCreditApproved,
				creditTerm: preApplicationCreditDate.creditTerm,
				applicantAmountSelected:
					state.amount > preApplicationCreditDate.authorizedAmount
						? preApplicationCreditDate.authorizedAmount
						: state.amount,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(PreApplicationActions.resetPreapplication, (): PreApplicationState => {
		remove(PREAPPLICATION_KEY);
		return initialState;
	}),
	on(
		PreApplicationActions.addDataFromSimulator,
		(state, simulatorData: SimulatorData): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				subProductId: simulatorData.subProductId,
				creditTerm: simulatorData.creditTerm,
				amount: simulatorData.amount,
				rate: simulatorData.rate,
				periodPayment: simulatorData.periodPayment,
				periodicityId: simulatorData.periodicityId,
				cat: simulatorData.cat,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addAuthorizedCreditDetails,
		(
			state,
			authorizedCreditDetails: AuthorizedCreditDetails
		): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				periodPayment: authorizedCreditDetails.perdiodPayment,
				cat: authorizedCreditDetails.cat,
				applicantAmountSelected:
					authorizedCreditDetails.applicantAmountSelected,
				totalAmountToPay: authorizedCreditDetails.totalAmountToPay,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addOtpCode,
		(state, payload): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				otpCode: payload.otpCode ?? '',
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addApplicationId,
		(state, payload): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				applicationId: payload.applicationId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addApplicantIdForNewLeads,
		(state, payload): PreApplicationState => {
			if (state.applicantType === ApplicantType.UNREGISTERED_LEAD) {
				const newState: PreApplicationState = {
					...state,
					applicantId: payload.applicantId,
				};
				saveStateToLocalStorage(newState);
				return newState;
			}
			return state;
		}
	),
	on(
		PreApplicationActions.loadPreApplicationDataFromDigitalProcess,
		(state, payload): PreApplicationState => {
			let newState: PreApplicationState;

			newState = {
				...state,
				...payload,
				productId:
					payload.productId === null || payload.productId <= 0
						? state.productId
						: payload.productId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addProductData,
		(state, payload: Product): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				productId: payload.productId,
				productName: payload.productName,
				subProductId: payload.subProductId,
				segmentId: payload.segmentId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addSocialContributionApplicationAndSchoolarshipInfo,
		(state, payload) => {
			const newState: PreApplicationState = {
				...state,
				isSocialContributionApplicable: payload.isSocialContributionApplicable,
				schoolarshipCertificateId: payload.schoolarshipCertificateId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addAmountFromInvestementSimulator,
		(state, payload): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				amount: payload.amount,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addProductAndSubproduct,
		(state, payload): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				productId: payload.productId,
				subProductId: payload.subProductId,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	),
	on(
		PreApplicationActions.addApplicantAddresses,
		(state, { addresses }): PreApplicationState => {
			const newState: PreApplicationState = {
				...state,
				applicantAddresses: addresses,
			};
			saveStateToLocalStorage(newState);
			return newState;
		}
	)
);

function saveStateToLocalStorage(state: PreApplicationState) {
	save<PreApplicationState>(PREAPPLICATION_KEY, state);
}
